/**
 * Supplier Invoice Lines Redux Slice
 * 
 * Estado y acciones para la gestión de líneas de facturas de proveedores
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { FilteredState, PaginationParams, SortParams, FilterParams } from '../types';
export interface SupplierInvoiceLine {
  id: number;
  invoice_header_id: number;
  entity_id: number | null;
  carrier_invoice_concept_id: number | null;
  quantity: number;
  unit_price: number | null;
  line_amount: number;
  tax_amount: number;
  is_active: boolean;
  created_at: string;
  created_by: string | null;
  modified_at: string | null;
  modified_by: string | null;
}
// Tipos de respuesta
interface SupplierInvoiceLinesResponse {
  data: SupplierInvoiceLine[];
  totalCount: number;
}
interface SupplierInvoiceLinesState extends FilteredState<SupplierInvoiceLine> {
  selectedLine: SupplierInvoiceLine | null;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
  // Para row spanning: agrupar por invoice_header_id
  groupedByInvoice: Record<number, SupplierInvoiceLine[]>;
}
const initialState: SupplierInvoiceLinesState = {
  items: [],
  totalCount: 0,
  currentPage: 1,
  pageSize: 100,
  loading: false,
  error: null,
  lastFetch: null,
  filters: {},
  sortField: 'invoice_header_id',
  sortDirection: 'desc',
  searchTerm: null,
  selectedLine: null,
  creating: false,
  updating: false,
  deleting: false,
  groupedByInvoice: {},
};
export const fetchSupplierInvoiceLines = createAsyncThunk<SupplierInvoiceLinesResponse, { pagination?: PaginationParams; sort?: SortParams; filters?: FilterParams; includeRelated?: boolean }>(
  'supplierInvoiceLines/fetchLines',
  async (params) => {
    throw new Error('Not implemented yet');
  }
);
export const fetchSupplierInvoiceLineById = createAsyncThunk<SupplierInvoiceLine, number>(
  'supplierInvoiceLines/fetchLineById',
  async (id) => {
    throw new Error('Not implemented yet');
  }
);
export const createSupplierInvoiceLine = createAsyncThunk<SupplierInvoiceLine, Partial<SupplierInvoiceLine>>(
  'supplierInvoiceLines/createLine',
  async (line) => {
    throw new Error('Not implemented yet');
  }
);
export const updateSupplierInvoiceLine = createAsyncThunk<{ id: number; data: SupplierInvoiceLine }, { id: number; data: Partial<SupplierInvoiceLine> }>(
  'supplierInvoiceLines/updateLine',
  async ({ id, data }) => {
    // TODO: Implementar llamada a API
    return { id, data: { ...data, id } as SupplierInvoiceLine };
  }
);
export const deleteSupplierInvoiceLine = createAsyncThunk<number, number>(
  'supplierInvoiceLines/deleteLine',
  async (id) => {
    // TODO: Implementar llamada a API
    return id;
  }
);
const supplierInvoiceLinesSlice = createSlice({
  name: 'supplierInvoiceLines',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<FilterParams>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.currentPage = 1;
    },
    clearFilters: (state) => {
      state.filters = {};
      state.searchTerm = null;
      state.currentPage = 1;
    },
    setSearchTerm: (state, action: PayloadAction<string | null>) => {
      state.searchTerm = action.payload;
      state.currentPage = 1;
    },
    setSort: (state, action: PayloadAction<SortParams>) => {
      state.sortField = action.payload.field;
      state.sortDirection = action.payload.direction;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1;
    },
    selectLine: (state, action: PayloadAction<SupplierInvoiceLine | null>) => {
      state.selectedLine = action.payload;
    },
    clearSelection: (state) => {
      state.selectedLine = null;
    },
    // Agrupar líneas por invoice_header_id para row spanning
    groupByInvoice: (state) => {
      const grouped: Record<number, SupplierInvoiceLine[]> = {};
      state.items.forEach((line) => {
        if (!grouped[line.invoice_header_id]) {
          grouped[line.invoice_header_id] = [];
        }
        grouped[line.invoice_header_id].push(line);
      });
      state.groupedByInvoice = grouped;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSupplierInvoiceLines.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSupplierInvoiceLines.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.lastFetch = Date.now();
        // Auto-agrupar después de cargar
        const grouped: Record<number, SupplierInvoiceLine[]> = {};
        action.payload.data.forEach((line) => {
          if (!grouped[line.invoice_header_id]) {
            grouped[line.invoice_header_id] = [];
          }
          grouped[line.invoice_header_id].push(line);
        });
        state.groupedByInvoice = grouped;
      })
      .addCase(fetchSupplierInvoiceLines.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Error al cargar líneas de factura';
      });
    builder
      .addCase(fetchSupplierInvoiceLineById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSupplierInvoiceLineById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedLine = action.payload;
        state.lastFetch = Date.now();
      })
      .addCase(fetchSupplierInvoiceLineById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Error al cargar línea de factura';
      });
    builder
      .addCase(createSupplierInvoiceLine.pending, (state) => {
        state.creating = true;
        state.error = null;
      })
      .addCase(createSupplierInvoiceLine.fulfilled, (state, action) => {
        state.creating = false;
        state.items.push(action.payload);
        state.totalCount += 1;
        // Actualizar agrupación
        const invoiceId = action.payload.invoice_header_id;
        if (!state.groupedByInvoice[invoiceId]) {
          state.groupedByInvoice[invoiceId] = [];
        }
        state.groupedByInvoice[invoiceId].push(action.payload);
      })
      .addCase(createSupplierInvoiceLine.rejected, (state, action) => {
        state.creating = false;
        state.error = action.error.message || 'Error al crear línea de factura';
      });
    builder
      .addCase(updateSupplierInvoiceLine.pending, (state) => {
        state.updating = true;
        state.error = null;
      })
      .addCase(updateSupplierInvoiceLine.fulfilled, (state, action: PayloadAction<{ id: number; data: SupplierInvoiceLine }>) => {
        state.updating = false;
        const index = state.items.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload.data;
        }
        if (state.selectedLine?.id === action.payload.id) {
          state.selectedLine = action.payload.data;
        }
        // Actualizar agrupación si cambió el invoice_header_id
        // TODO: Implementar lógica de reagrupación
      })
      .addCase(updateSupplierInvoiceLine.rejected, (state, action) => {
        state.updating = false;
        state.error = action.error.message || 'Error al actualizar línea de factura';
      });
    builder
      .addCase(deleteSupplierInvoiceLine.pending, (state) => {
        state.deleting = true;
        state.error = null;
      })
      .addCase(deleteSupplierInvoiceLine.fulfilled, (state, action) => {
        state.deleting = false;
        const deletedLine = state.items.find((item) => item.id === action.payload);
        state.items = state.items.filter((item) => item.id !== action.payload);
        state.totalCount -= 1;
        if (state.selectedLine?.id === action.payload) {
          state.selectedLine = null;
        }
        // Actualizar agrupación
        if (deletedLine) {
          const invoiceId = deletedLine.invoice_header_id;
          if (state.groupedByInvoice[invoiceId]) {
            state.groupedByInvoice[invoiceId] = state.groupedByInvoice[invoiceId].filter(
              (line) => line.id !== action.payload
            );
            if (state.groupedByInvoice[invoiceId].length === 0) {
              delete state.groupedByInvoice[invoiceId];
            }
          }
        }
      })
      .addCase(deleteSupplierInvoiceLine.rejected, (state, action) => {
        state.deleting = false;
        state.error = action.error.message || 'Error al eliminar línea de factura';
      });
  },
});
export const {
  setFilters,
  clearFilters,
  setSearchTerm,
  setSort,
  setPage,
  setPageSize,
  selectLine,
  clearSelection,
  groupByInvoice,
} = supplierInvoiceLinesSlice.actions;
export default supplierInvoiceLinesSlice.reducer;
